import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { fitTitlePx, fitDescriptionPx } from '../lib/seoPixelHelper';

const DEFAULT_TITLE = 'Autoriseret Kloakmester & Entreprenør | Jan Nørgaard';
const DEFAULT_DESC = 'Autoriseret kloakmester og entreprenør Jan Nørgaard ApS med over 20 års erfaring. Kloakspuling, dræn, belægning og støbearbejde. Få et tilbud.';
const DEFAULT_IMAGE = 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/kloakspuling-og-tv-inspektion.jpg';
const SITE_BRAND = 'Jan Nørgaard';
const SITE_NAME = 'Entreprenør Jan Nørgaard ApS';

function setMetaTag(selector, attributeName, attributeValue, content) {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  if (selector.startsWith('link')) {
    element.setAttribute('href', content);
  } else {
    element.setAttribute('content', content);
  }
}

/**
 * Computes a title guaranteed to be max 512px in Google SERP font (20px Arial)
 */
function buildSafeTitle(title) {
  if (!title) return fitTitlePx(DEFAULT_TITLE, 512);

  const trimmed = title.trim();
  // Check if title already includes brand identifier
  const hasBrand = /Jan Nørgaard/i.test(trimmed);

  if (hasBrand) {
    return fitTitlePx(trimmed, 512);
  }

  // If brand is missing, attempt to append " | Jan Nørgaard" if within 512px
  const candidateWithBrand = `${trimmed} | ${SITE_BRAND}`;
  if (fitTitlePx(candidateWithBrand, 512) === candidateWithBrand) {
    return candidateWithBrand;
  }

  // If full candidate exceeds 512px, keep trimmed title within 512px
  return fitTitlePx(trimmed, 512);
}

/**
 * Computes a description guaranteed to be max 970px in Google SERP font (14px Arial)
 */
function buildSafeDescription(description) {
  const candidate = (description || DEFAULT_DESC).trim();
  return fitDescriptionPx(candidate, 970);
}

export default function SEO({
  title,
  description,
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  canonicalUrl,
}) {
  const location = useLocation();

  useEffect(() => {
    // 1. Page Title (strictly <= 512px in 20px Arial)
    const finalTitle = buildSafeTitle(title);
    document.title = finalTitle;

    // 2. Meta Description (strictly <= 970px in 14px Arial)
    const finalDescription = buildSafeDescription(description);
    setMetaTag('meta[name="description"]', 'name', 'description', finalDescription);

    // 3. Robots
    const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';
    setMetaTag('meta[name="robots"]', 'name', 'robots', robotsContent);

    // 4. Canonical URL
    const domain = typeof window !== 'undefined' ? window.location.origin : 'https://jannorgaard.dk';
    const finalCanonical = canonicalUrl || `${domain}${location.pathname}`;
    setMetaTag('link[rel="canonical"]', 'rel', 'canonical', finalCanonical);

    // 5. Open Graph
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', finalTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', finalDescription);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', type);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', finalCanonical);
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', image);
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
    setMetaTag('meta[property="og:locale"]', 'property', 'og:locale', 'da_DK');

    // 6. Twitter Card
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', finalTitle);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', finalDescription);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', image);
  }, [title, description, image, type, noindex, canonicalUrl, location.pathname]);

  return null;
}
